import { Controller } from '@hotwired/stimulus'
import { debounce, divide, find, round } from 'lodash'

import { closeFlashAlert, flashAlert } from '../../shared/flash'

export default class extends Controller {

  static targets = [
    'amount',
    'amountContainer',
    'checkNumber',
    'cardContainer',
    'cardError',
    'cardErrorContainer',
    'cardElement',
    'cardholderName',
    'checkTitle',
    'container',
    'methodSelect',
    'remove',
  ]

  initialize() {
    this.fetchHeaders = {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    }
    this.stripe = window.Stripe
    this.validateCardholderName = debounce(this.validateCardholderName, 300)
    this.onStripeFormChange = debounce(this.onStripeFormChange, 300)
    this.resetError = debounce(this.resetError, 300)
    this.amountChange = debounce(this.amountChange, 100)
    this.dispatchCheckUpdate = debounce(this.dispatchCheckUpdate, 100)
    this.createIntent = debounce(this.createIntent, 300)
    this.updateIntent = debounce(this.updateIntent, 300)


    this.order = { id: parseInt(this.containerTarget.dataset.orderId) }
    this.originalIndex = parseInt(this.containerTarget.dataset.originalIndex)
    this.resetCheck()
  }

  connect() {
    this.resetError()
    this.checkoutContainer = document.querySelector('#checkout-container')
    this.checkoutContainer.addEventListener('orderLoaded', this.onOrderLoad)
    this.checkoutContainer.addEventListener('splitModeUpdate', this.onSplitModeUpdate)
    this.checkoutContainer.addEventListener('cardErrors', this.onCardErrors)
    this.checkoutContainer.addEventListener('checksOrdered', this.onChecksOrdered)
  }

  onChecksOrdered = ({ detail }) => {
    const { checks } = detail
    const matchingCheck = find(checks, ['originalIndex', this.originalIndex])
    if (matchingCheck) {
      this.maxAmount = matchingCheck?.maxAmount
      this.checkTitleTarget.innerText = `Check ${ matchingCheck.order }`
      this.removeTarget.disabled = checks.length <= 1
    } else {
      this.checkTitleTarget.innerText = 'Split Payment'
    }
  }

  onCardErrors = ({ detail }) => {
    const errorDetail = find(detail, ['webIntentId', this.webIntentId])
    if (errorDetail) {
      const { message, declineCode } = errorDetail
      this.error = declineCode ? `${message} (${declineCode})` : `${message}`
      this.formError(this.error)
    }
  }

  onOrderLoad = ({ detail }) => {
    this.order = detail.order
    if (!this.splitModeActive && this.originalIndex === 0) {
      this.setAmountInputValue(this.order.total_cents)
    }
  }

  onSplitModeUpdate = ({ detail }) => {
    this.splitModeActive = detail.splitModeActive
    this.checkNumberTarget.hidden = !this.splitModeActive
    this.amountContainerTarget.hidden = !this.splitModeActive

    if (!this.splitModeActive) {
      this.setAmountInputValue(this.order?.total_cents || 0)
      if (detail.activeIndex !== this.originalIndex) {
        this.resetCheck()
      }
    } else {
      this.setAmountInputValue(0)
    }
    this.setComplete()
  }

  dispatchCheckUpdate() {
    this.setComplete()
    const { originalIndex, complete, card, clientSecret, webIntentId } = this
    const detail = {
      amountCents: this.safeInputValueToCents(),
      card,
      cardholderName: this.cardholderNameTarget.value,
      clientSecret,
      complete,
      originalIndex,
      paymentMethod: this.methodSelectTarget.value,
      webIntentId,
    }
    const checkUpdateEvent = new CustomEvent('checkUpdate', { detail })
    this.checkoutContainer.dispatchEvent(checkUpdateEvent)
  }

  setAmountInputValue(amountCents) {
    this.amountTarget.value = divide(amountCents, 100).toFixed(2)
  }

  safeInputValueToCents() {
    const numValue = parseFloat(this.amountTarget.value) * 100
    return round(numValue)
  }

  resetCheck() {
    if (this.card) {
      this.card.unmount()
    }
    this.methodSelectTarget.value = ''
    this.setAmountInputValue(0)
    this.cardholderNameTarget.value = ''
    this.cardContainerTarget.classList.add('hidden')
    this.webIntentId = null
    this.complete = false
    this.stripeComplete = false
    this.clientSecret = null
    this.nameTouched = false
    this.error = null
  }

  removeCheck = _event => {
    if (window.confirm( // eslint-disable-line no-alert
      'Are you sure? Removing this check will remove all of its payment information.'
    )) {
      this.resetCheck()
      this.resetError()
      const detail = { originalIndex: this.originalIndex }
      const removeCheckEvent = new CustomEvent('removeCheck', { detail })
      this.checkoutContainer.dispatchEvent(removeCheckEvent)
    }
  }

  amountChange = _event => {
    const safeAmountCents = this.safeInputValueToCents()

    if (!this.amountTarget.value || this.amountTarget.value <= 0) {
      this.setAmountInputValue(0)
    } else if (this.maxAmount && safeAmountCents > this.maxAmount) {
      this.setAmountInputValue(this.maxAmount)
    } else {
      this.setAmountInputValue(safeAmountCents)
    }

    if (this.methodSelectTarget.value === 'web_intent') {
      if (this.webIntentId) {
        this.updateIntent()
      } else {
        this.createIntent()
      }
    } else {
      this.dispatchCheckUpdate()
    }
  }

  methodSelected(_event) {
    this.resetError()
    this.cardContainerTarget.classList.add('hidden')
    if (this.card) {
      this.card.unmount()
    }
    this.amountTarget.disabled = false

    if (this.methodSelectTarget.value === 'web_intent') {
      this.showCardElement()
      this.cardContainerTarget.classList.remove('hidden')

      if (this.amountTarget.value > 0) {
        this.createIntent()
      }
    } else if (this.methodSelectTarget.value === 'reserve') {
      this.setAmountInputValue(0)
      this.amountTarget.disabled = true
    }
    this.dispatchCheckUpdate()
  }

  setComplete() {
    const inputCents = parseInt(this.amountTarget.value * 100)

    switch (this.methodSelectTarget.value) {
    case 'web_intent':
      this.complete = this.nameTouched &&
        !!this.cardholderNameTarget.value &&
        this.stripeComplete &&
        inputCents > 0
      break
    case 'cash':
      this.complete = inputCents >= 0
      break
    case 'comp':
      this.complete = inputCents >= 0
      break
    case 'reserve':
      this.complete = inputCents === 0
      break
    default:
      this.complete = false
    }
  }

  formError(message) {
    this.cardErrorTarget.textContent = `${message}`
    this.cardErrorContainerTarget.classList.remove('hidden')
    this.cardContainerTarget.classList.remove('border-gray-200')
    this.cardContainerTarget.classList.add('border-red-500')
    flashAlert('Something went wrong with payment. See below for details.')
  }

  resetError = () => {
    this.cardErrorTarget.textContent = ''
    this.cardErrorContainerTarget.classList.add('hidden')
    this.cardholderNameTarget.classList.remove('invalid')
    this.cardContainerTarget.classList.remove('border-red-500')
    this.cardContainerTarget.classList.add('border-gray-200')
    closeFlashAlert()
  }

  showCardElement() {
    this.card = this.card || this.stripe.elements().create('card')
    this.card.mount(this.cardElementTarget)
    this.card.on('change', event => this.onStripeFormChange(event))
    this.cardContainerTarget.classList.remove('hidden')
  }

  onStripeFormChange = event => {
    if (event.error) {
      this.stripeComplete = false
      this.error = event.error.message
      this.formError(this.error);
    } else if (event.complete) {
      this.resetError()
      this.stripeComplete = true
      this.error = null
      if (this.nameTouched) {
        this.validateCardholderName()
      }
    }

    this.dispatchCheckUpdate()
  }

  validateCardholderName = () => {
    this.nameTouched = true
    if (!this.cardholderNameTarget.value.trim()) {
      this.cardholderNameTarget.classList.add('invalid')
      this.formError('You must enter a valid cardholder name.')
    } else {
      this.resetError()
    }
    this.dispatchCheckUpdate()
  }

  createIntent() {
    this.resetError()
    const amount_cents = this.safeInputValueToCents()
    if (amount_cents <= 0) {
      return
    }

    const requestParams = { amount_cents }

    fetch(`/sandbar/admin/orders/${this.order.id}/intents`, {
      method: 'POST',
      headers: this.fetchHeaders,
      body: JSON.stringify(requestParams),
    })
      .then(resp => {
        if (resp.ok) {
          resp.json().then(intent => {
            this.clientSecret = intent.client_secret
            this.webIntentId = intent.id
            this.dispatchCheckUpdate()
          })
        } else {
          resp.json().then(err => {
            this.clientSecret = null
            this.dispatchCheckUpdate()
            this.handleError(err)
          })
        }
      })
      .catch(err => this.handleError(err))
  }

  updateIntent() {
    if (!this.webIntentId) {
      return
    }
    this.checkoutContainer.dispatchEvent(new Event('intentUpdateStart'))

    const amount_cents = this.safeInputValueToCents()
    const requestParams = { amount_cents }

    fetch(`/sandbar/admin/orders/${this.order.id}/intents/${this.webIntentId}`, {
      method: 'PUT',
      headers: this.fetchHeaders,
      body: JSON.stringify(requestParams),
    })
      .then(resp => {
        if (resp.ok) {
          resp.json().then(intent => {
            this.clientSecret = intent.client_secret
            this.setAmountInputValue(intent.amount)
            this.dispatchCheckUpdate()
          })
        } else {
          resp.json().then(err => {
            this.dispatchCheckUpdate()
            this.handleError(err)
          })
        }
      })
      .catch(err => {
        this.dispatchCheckUpdate()
        this.handleError(err)
      })
  }

  handleError(error) {
    console.error({ error })
    this.formError(error?.message || error?.error || error)
  }
}
